import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PdMark from '../components/PdMark';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Brand = () => (
  <Link to="/" className="inline-flex items-center gap-3 rounded-md" aria-label="Precious Data home">
    <PdMark size={28} className="text-brand" />
    <span className="font-heading text-[18px] font-bold tracking-[-0.02em] text-ink">Precious Data</span>
  </Link>
);

export const Login = () => {
  const navigate = useNavigate();
  const { login, verifyMfa, mfaPending } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.requiresMfa) {
        toast.info('Enter the code from your authenticator app');
      } else {
        toast.success('Signed in');
        navigate('/choose-account');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'That email or password is not right');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    if (mfaCode.length !== 6) return;

    setLoading(true);
    try {
      await verifyMfa(mfaCode);
      toast.success('Signed in');
      navigate('/choose-account');
    } catch {
      toast.error('That code is not right. Try the next one your app shows.');
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  if (mfaPending) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="mb-6"><Brand /></div>

          <div className="bg-surface rounded-xl p-7 sm:p-9">
            <h1 className="font-heading text-[28px] font-bold tracking-[-0.035em] leading-[1.05]">
              Enter your authentication code
            </h1>
            <p className="text-ink-70 mt-3">
              Open your authenticator app and type the six-digit code it shows.
            </p>
            <div className="flex flex-col items-center gap-6 mt-7">
              <InputOTP
                value={mfaCode}
                onChange={setMfaCode}
                maxLength={6}
                data-testid="mfa-code-input"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>

              <Button
                onClick={handleMfaVerify}
                disabled={mfaCode.length !== 6 || loading}
                className="w-full"
                data-testid="verify-mfa-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ground flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6"><Brand /></div>

        <div className="bg-surface rounded-xl p-7 sm:p-9">
          <h1 className="font-heading text-[28px] font-bold tracking-[-0.035em] leading-[1.05]">Sign in</h1>
          <p className="text-ink-70 mt-3">
            Your password is also the key to your records. It never leaves this device.
          </p>

          <form onSubmit={handleLogin} className="space-y-5 mt-7">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[15px] font-semibold block">What&apos;s your email address?</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="username"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[15px] font-semibold block">And your password?</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <div className="mt-6 space-y-2 text-[15px]">
            <p className="text-ink-70">
              New here?{' '}
              <Link to="/register" className="text-brand font-medium hover:underline">
                Create an account
              </Link>
            </p>
            <p className="text-ink-70">
              <Link to="/recover" className="text-brand font-medium hover:underline" data-testid="forgot-password-link">
                Forgotten your password? Use your recovery code
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
